import type { ColumnDef } from '@tanstack/react-table'
import {
  CircleCheck,
  CircleSlash,
  EllipsisVertical,
  LoaderCircle,
  Pencil,
  Plus,
  Star,
  Table2,
  Tag,
} from 'lucide-react'
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  type FiltroDeAtivo,
  type TabelaDePreco,
  useAtualizarTabelaDePreco,
  useDesativarTabelaDePreco,
  useTabelasDePreco,
  vigenciaEmTexto,
} from '@/features/precos/api'
import { DialogoDeTabela } from '@/features/precos/dialogo-de-tabela'
import { EditorDeTarifas } from '@/features/precos/editor-de-tarifas'
import { errorMessage } from '@/lib/api'
import { usePermissao } from '@/lib/permissoes'

/**
 * Tabelas de preço.
 *
 * ---------------------------------------------------------------------------
 * UMA TABELA É UMA POLÍTICA COMERCIAL, NÃO UMA LISTA DE NÚMEROS
 * ---------------------------------------------------------------------------
 * A tabela PADRÃO é a da vitrine: ela precifica todo cliente que não tem tabela
 * negociada. Existe exatamente uma ativa por locadora, garantida por índice
 * único no banco — e a troca nunca acontece sozinha, porque trocá-la muda o
 * preço de todo mundo de uma vez. Por isso a lista mostra, em cada linha, o que
 * quebra ao mexer nela: quantas categorias ela cobre e quantos clientes
 * corporativos dependem dela.
 *
 * As duas ações que a API recusa não são oferecidas como se fossem possíveis:
 * desativar a tabela da vitrine e desativar tabela com cliente pendurado
 * abrem a confirmação já explicando por que não dá e sem o botão vermelho. Um
 * clique que só produz 409 é a pior forma de ensinar uma regra de negócio.
 *
 * ---------------------------------------------------------------------------
 * O AMARELO APARECE UMA VEZ
 * ---------------------------------------------------------------------------
 * No CTA "Nova tabela". Nada mais nesta tela usa a cor da marca: situação é
 * verde/cinza semântico, alerta é âmbar, recusa é vermelho. Uma tela que é uma
 * grade de dinheiro fica ilegível se cada número disputar atenção — o olho
 * precisa parar no botão que cria a política, e depois nos números.
 */

const TAMANHO_DA_PAGINA = 20

/**
 * Os dois estados explícitos do filtro tri-estado. `''` (todos) fica de fora
 * porque é a ausência de filtro — e `montarQuery` descarta string vazia.
 */
const FILTROS_DE_ATIVO = ['true', 'false'] as const

/** Por que a desativação está bloqueada. Espelha os dois motivos do 409 da API. */
type BloqueioDaDesativacao = 'PADRAO' | 'CONTAS_NEGOCIADAS'

function bloqueioDe(tabela: TabelaDePreco | null): BloqueioDaDesativacao | null {
  if (tabela === null) {
    return null
  }

  if (tabela.padrao) {
    return 'PADRAO'
  }

  return tabela.contasNegociadas > 0 ? 'CONTAS_NEGOCIADAS' : null
}

/** Selo da tabela da vitrine. `outline`, nunca `brand`: amarelo aqui é o CTA. */
function BadgeDeSituacao({ tabela }: { readonly tabela: TabelaDePreco }): ReactElement {
  if (!tabela.ativo) {
    return (
      <Badge variant="neutral" title="Não cota mais — cotações antigas continuam válidas">
        <CircleSlash aria-hidden="true" />
        Fora do ar
      </Badge>
    )
  }

  return (
    <Badge variant="success">
      <CircleCheck aria-hidden="true" />
      No ar
    </Badge>
  )
}

interface DialogoDeDesativacaoProps {
  /** `null` mantém fechado. */
  readonly tabela: TabelaDePreco | null
  readonly aoFechar: () => void
}

/**
 * Confirmação da desativação.
 *
 * A tabela não é apagada: toda cotação emitida aponta para ela, e o histórico é
 * o que responde "por que cobramos isso em março". O diálogo diz isso, diz o
 * que muda (ela para de cotar) e, nos dois casos em que a API recusa, mostra o
 * motivo ANTES do clique em vez de deixar a pessoa colher um 409.
 */
function DialogoDeDesativacao({ tabela, aoFechar }: DialogoDeDesativacaoProps): ReactElement {
  const desativar = useDesativarTabelaDePreco()
  const [erro, setErro] = useState<string | null>(null)
  const ocupado = desativar.isPending
  const bloqueio = bloqueioDe(tabela)

  // O diálogo é reaproveitado por todas as linhas: sem esta limpeza, a falha ao
  // desativar a tabela A apareceria na confirmação da tabela B.
  useEffect(() => {
    setErro(null)
  }, [tabela])

  function confirmar(): void {
    if (tabela === null) {
      return
    }

    setErro(null)

    desativar.mutate(tabela.id, {
      onSuccess: () => {
        toast.success(`${tabela.nome} saiu do ar`, {
          description: 'Ela para de cotar. Nada foi apagado — cotações antigas seguem explicadas.',
        })
        aoFechar()
      },
      // O erro fica NO diálogo: um toast sumiria e a tela voltaria a perguntar
      // a mesma coisa, como se nada tivesse acontecido.
      onError: (causa: unknown) => {
        setErro(errorMessage(causa))
      },
    })
  }

  return (
    <Dialog
      open={tabela !== null}
      onOpenChange={(proximo) => {
        if (!proximo && !ocupado) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tirar {tabela?.nome ?? 'a tabela'} do ar?</DialogTitle>
          <DialogDescription>
            A tabela para de cotar novas locações. A linha continua existindo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm text-fg-secondary">
          {erro === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          )}

          {bloqueio === 'PADRAO' ? (
            <Alert variant="warning">
              <AlertTitle>Esta é a tabela da vitrine</AlertTitle>
              <AlertDescription>
                Sem ela, quem não tem tabela negociada fica sem preço nenhum — e a cotação passaria
                a falhar por ausência de dado, que é a forma mais confusa de um sistema de preço
                quebrar. Marque outra tabela como padrão e volte aqui.
              </AlertDescription>
            </Alert>
          ) : bloqueio === 'CONTAS_NEGOCIADAS' ? (
            <Alert variant="warning">
              <AlertTitle>
                {tabela?.contasNegociadas === 1
                  ? 'Um cliente usa esta tabela como tabela negociada'
                  : `${String(tabela?.contasNegociadas ?? 0)} clientes usam esta tabela como tabela negociada`}
              </AlertTitle>
              <AlertDescription>
                Desativá-la jogaria essas contas de volta na tabela cheia sem aviso, e a primeira
                notícia seria uma fatura mais cara. Aponte-as para outra tabela antes.
              </AlertDescription>
            </Alert>
          ) : (
            <p>
              Nada é apagado: toda cotação já emitida continua apontando para esta tabela, que é o
              que responde “por que cobramos isso em março”. Reativar depois é um clique em editar.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
            {bloqueio === null ? 'Cancelar' : 'Entendi'}
          </Button>

          {/* Botão que só produziria 409 não é oferecido cinza: ele não existe. */}
          {bloqueio === null ? (
            <Button type="button" variant="destructive" onClick={confirmar} disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Tirar do ar
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function PrecosPage(): ReactElement {
  const podeCriar = usePermissao('price_table.create')
  const podeEditar = usePermissao('price_table.update')
  const podeDesativar = usePermissao('price_table.delete')

  const [pagina, setPagina] = useState(1)
  const [ativo, setAtivo] = useState<FiltroDeAtivo>('')
  const [padrao, setPadrao] = useState<FiltroDeAtivo>('')
  const [vigenteEm, setVigenteEm] = useState('')

  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [emEdicao, setEmEdicao] = useState<TabelaDePreco | undefined>(undefined)
  const [paraDesativar, setParaDesativar] = useState<TabelaDePreco | null>(null)
  const [selecionadaId, setSelecionadaId] = useState<string | null>(null)

  // Em 375px o editor fica abaixo da lista inteira: sem levar a página até ele,
  // "Ver tarifas" não parece ter feito nada.
  const secaoDasTarifas = useRef<HTMLDivElement>(null)

  const filtros = useMemo(
    () => ({ pagina, tamanho: TAMANHO_DA_PAGINA, ativo, padrao, vigenteEm }),
    [pagina, ativo, padrao, vigenteEm],
  )

  const consulta = useTabelasDePreco(filtros)
  // `mutate` é referencialmente estável no TanStack Query; o objeto devolvido
  // pelo hook não é. Desestruturar é o que permite às colunas declararem
  // dependências honestas em vez de silenciar o `exhaustive-deps`.
  const { mutate: mutarTabela, isPending: marcandoVitrine } = useAtualizarTabelaDePreco()

  const dados = consulta.data
  const itens = dados?.itens ?? []

  /**
   * A tabela que o editor mostra sai da LISTA fresca, e não de um objeto
   * guardado em estado: assim renomear a tabela aparece no editor no mesmo
   * instante. Sem seleção — ou quando a selecionada sai do filtro — o editor
   * acompanha a lista, e a primeira linha é a padrão (o servidor a ordena
   * primeiro), que é o que o gestor procura em nove de cada dez visitas.
   */
  const tabelaDoEditor = itens.find((item) => item.id === selecionadaId) ?? itens[0] ?? null

  const temFiltro = ativo !== '' || padrao !== '' || vigenteEm !== ''

  function limparFiltros(): void {
    setAtivo('')
    setPadrao('')
    setVigenteEm('')
    setPagina(1)
  }

  function abrirCriacao(): void {
    setEmEdicao(undefined)
    setDialogoAberto(true)
  }

  const abrirEdicao = useCallback((tabela: TabelaDePreco): void => {
    setEmEdicao(tabela)
    setDialogoAberto(true)
  }, [])

  const verTarifas = useCallback((tabela: TabelaDePreco): void => {
    setSelecionadaId(tabela.id)
    secaoDasTarifas.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  /**
   * Marcar a vitrine é `PATCH` de um campo só — o servidor NÃO desmarca a
   * outra, de propósito. O 409 traz a instrução pronta ("desmarque a atual
   * antes"), e é ela que aparece no toast: reescrevê-la aqui seria uma segunda
   * versão da mesma regra, divergindo no dia em que a API mudar de ideia.
   */
  const marcarComoPadrao = useCallback(
    (tabela: TabelaDePreco): void => {
      mutarTabela(
        { id: tabela.id, dados: { padrao: true } },
        {
          onSuccess: () => {
            toast.success(`${tabela.nome} é a nova tabela da vitrine`, {
              description: 'Todo cliente sem tabela negociada passa a ser cotado por ela.',
            })
          },
          onError: (causa: unknown) => {
            toast.error('Não foi possível marcar como padrão', { description: errorMessage(causa) })
          },
        },
      )
    },
    [mutarTabela],
  )

  const colunas = useMemo<ColumnDef<TabelaDePreco>[]>(
    () => [
      {
        id: 'nome',
        header: 'Tabela',
        accessorFn: (linha: TabelaDePreco) => linha.nome,
        cell: ({ row }) => {
          const tabela: TabelaDePreco = row.original

          return (
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">{tabela.nome}</p>
              {tabela.descricao === null ? null : (
                <p className="truncate text-xs text-fg-secondary">{tabela.descricao}</p>
              )}
              {tabela.padrao ? (
                <span className="mt-1 inline-flex">
                  <Badge variant="outline" title="Precifica todo cliente sem tabela negociada">
                    <Star aria-hidden="true" />
                    Vitrine (padrão)
                  </Badge>
                </span>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'vigencia',
        header: 'Vigência',
        accessorFn: (linha: TabelaDePreco) => linha.vigenteDe,
        cell: ({ row }) => {
          const tabela: TabelaDePreco = row.original

          return (
            <span className="whitespace-nowrap text-sm tabular-money text-foreground">
              {vigenciaEmTexto(tabela.vigenteDe, tabela.vigenteAte)}
            </span>
          )
        },
      },
      {
        id: 'cobertura',
        header: 'Cobertura',
        accessorFn: (linha: TabelaDePreco) => linha.categoriasPrecificadas,
        cell: ({ row }) => {
          const tabela: TabelaDePreco = row.original

          return (
            <div className="min-w-0">
              <p className="whitespace-nowrap text-sm tabular-money text-foreground">
                {tabela.categoriasPrecificadas === 1
                  ? '1 categoria'
                  : `${String(tabela.categoriasPrecificadas)} categorias`}
              </p>
              <p className="whitespace-nowrap text-xs tabular-money text-fg-secondary">
                {tabela.tarifas === 1 ? '1 faixa' : `${String(tabela.tarifas)} faixas`}
                {tabela.sazonalidades === 0
                  ? ''
                  : ` · ${String(tabela.sazonalidades)} sazonalidades`}
              </p>
            </div>
          )
        },
      },
      {
        id: 'negociadas',
        header: 'Clientes',
        accessorFn: (linha: TabelaDePreco) => linha.contasNegociadas,
        cell: ({ row }) => {
          const tabela: TabelaDePreco = row.original

          return (
            <span className="whitespace-nowrap text-sm tabular-money text-fg-secondary">
              {tabela.contasNegociadas === 0
                ? 'nenhum'
                : tabela.contasNegociadas === 1
                  ? '1 negociada'
                  : `${String(tabela.contasNegociadas)} negociadas`}
            </span>
          )
        },
      },
      {
        id: 'situacao',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeSituacao tabela={row.original} />,
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const tabela: TabelaDePreco = row.original
          const podeVirarPadrao = podeEditar && !tabela.padrao && tabela.ativo
          const podeSairDoAr = podeDesativar && tabela.ativo

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações da tabela ${tabela.nome}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onSelect={() => {
                    verTarifas(tabela)
                  }}
                >
                  <Table2 aria-hidden="true" />
                  Ver tarifas por faixa
                </DropdownMenuItem>

                {podeEditar ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      abrirEdicao(tabela)
                    }}
                  >
                    <Pencil aria-hidden="true" />
                    Editar tabela
                  </DropdownMenuItem>
                ) : null}

                {podeVirarPadrao ? (
                  // Só existe UMA padrão ativa: dois cliques rápidos viram dois
                  // `PATCH`, e o segundo colide com o primeiro num 409 que a
                  // pessoa não causou.
                  <DropdownMenuItem
                    disabled={marcandoVitrine}
                    onSelect={() => {
                      marcarComoPadrao(tabela)
                    }}
                  >
                    <Star aria-hidden="true" />
                    Marcar como vitrine
                  </DropdownMenuItem>
                ) : null}

                {podeSairDoAr ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => {
                        setParaDesativar(tabela)
                      }}
                    >
                      <CircleSlash aria-hidden="true" />
                      Tirar do ar
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    [podeEditar, podeDesativar, marcandoVitrine, abrirEdicao, marcarComoPadrao, verTarifas],
  )

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Tabelas de preço</h1>
          <p className="max-w-prose text-sm text-fg-secondary">
            A diária de cada categoria por faixa de duração. A tabela padrão é a da vitrine — existe
            uma só, e é ela que precifica quem não tem tabela negociada.
          </p>
        </div>

        {podeCriar ? (
          <Button onClick={abrirCriacao}>
            <Plus aria-hidden="true" />
            Nova tabela
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="filtro-situacao-tabelas">Situação</Label>
          <Select
            id="filtro-situacao-tabelas"
            value={ativo}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setAtivo(FILTROS_DE_ATIVO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todas</option>
            <option value="true">No ar</option>
            <option value="false">Fora do ar</option>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-vitrine-tabelas">Uso</Label>
          <Select
            id="filtro-vitrine-tabelas"
            value={padrao}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setPadrao(FILTROS_DE_ATIVO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Vitrine e negociadas</option>
            <option value="true">Só a da vitrine</option>
            <option value="false">Só as negociadas</option>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-vigencia-tabelas">Vigente em</Label>
          <Input
            id="filtro-vigencia-tabelas"
            type="date"
            value={vigenteEm}
            onChange={(evento) => {
              setPagina(1)
              setVigenteEm(evento.target.value)
            }}
            className="tabular-money"
          />
        </div>
      </div>

      <TabelaDeDados
        colunas={colunas}
        dados={itens}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Tabelas de preço da locadora"
        estadoVazio={
          <EmptyState
            icone={Tag}
            titulo={temFiltro ? 'Nenhuma tabela com esses filtros' : 'Nenhuma tabela de preço'}
            descricao={
              temFiltro
                ? 'Ajuste a situação, o uso ou a data de vigência para ver outros resultados.'
                : 'Sem tabela não há cotação: crie a tabela da vitrine e preencha a diária de cada categoria por faixa de duração.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={abrirCriacao}>
                  <Plus aria-hidden="true" />
                  Nova tabela
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />

      <Separator />

      <div ref={secaoDasTarifas}>
        {/* A tabela do editor sai da lista: enquanto ela carrega (ou falha),
            `tabelaDoEditor` é `null` — e sem esta informação o editor diria
            "escolha uma tabela na lista acima", que é um estado VAZIO no
            lugar de um de carregamento ou de erro. */}
        <EditorDeTarifas
          tabela={tabelaDoEditor}
          listaDeTabelas={consulta.isPending ? 'carregando' : consulta.isError ? 'erro' : 'pronta'}
          podeEditar={podeEditar}
          podeRemover={podeDesativar}
        />
      </div>

      {podeCriar || podeEditar ? (
        <DialogoDeTabela
          aberto={dialogoAberto}
          tabela={emEdicao}
          aoFechar={() => {
            setDialogoAberto(false)
          }}
        />
      ) : null}

      {podeDesativar ? (
        <DialogoDeDesativacao
          tabela={paraDesativar}
          aoFechar={() => {
            setParaDesativar(null)
          }}
        />
      ) : null}
    </div>
  )
}
